import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Observable } from 'rxjs';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { map, distinctUntilChanged, filter } from 'rxjs/operators';
import { CoreDataService } from '@app/core/async-services/http/core.data';
import { API } from '@app/core/models';
import { Announcements } from '@app/core/async-services/http/versioned/interfaces';
import { VersionConfigService } from '@app/core/services/version-config';
import { CustomEncoder } from '../http.util';
import { filterTruthy } from '@app/core/utils/rxjs.util';

@Injectable({
  providedIn: 'root',
})
export class AnnouncementHttpService {
  private _urlPrefix!: string;

  constructor(
    private _versionConfigService: VersionConfigService,
    private _coreDataService: CoreDataService,
    private _platform: Platform,
  ) {
    this._versionConfigService
      .getVersionConfig()
      .pipe(
        filterTruthy(),
        filter((config) => {
          if (config.api?.active) {
            return true;
          }
          return false;
        }),
        map((config) => config.api?.prefix.announcement),
        distinctUntilChanged(),
      )
      .subscribe((version) => {
        this._urlPrefix = `${version}/announcement/`;
      });
  }

  get urlPrefix() {
    return this._urlPrefix;
  }

  getAnnouncementManagers() {
    const options: API.HttpOptions = {
      headers: new HttpHeaders(),
      withCredentials: true,
    };
    return this._coreDataService.get<Announcements.ManagersResponse>(
      `${this._urlPrefix}managers`,
      options,
    );
  }

  getAnnouncementHotels() {
    const options: API.HttpOptions = {
      headers: new HttpHeaders(),
      withCredentials: true,
    };
    return this._coreDataService.get<Announcements.HotelsResponse>(
      `${this._urlPrefix}hotels`,
      options,
    );
  }

  getHotelEmployees(entID: string, departmentID?: string) {
    const options: API.HttpOptions = {
      headers: new HttpHeaders(),
      withCredentials: true,
    };

    options.params = this._coreDataService.generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      departmentID
        ? {
            departmentID,
            entID,
          }
        : {
            entID,
          },
    );

    return this._coreDataService.get<Announcements.EmployeesResponse>(
      `${this._urlPrefix}hotel-employees`,
      options,
    );
  }

  postAnnouncement(
    body: Announcements.CreateRequestBody,
  ): Observable<Announcements.AnnouncementResponse> {
    const options: API.HttpOptions = !this._platform.is('capacitor')
      ? {
          headers: new HttpHeaders(),
          withCredentials: true,
        }
      : {};

    return this._coreDataService.post<Announcements.AnnouncementResponse>(
      `${this._urlPrefix}create`,
      body,
      options,
    );
  }

  getRecipientCount(body: Announcements.RecipientCountRequestBody) {
    const options: API.HttpOptions = !this._platform.is('capacitor')
      ? {
          headers: new HttpHeaders(),
          withCredentials: true,
        }
      : {};
    return this._coreDataService.post<Announcements.RecipientCountResponse>(
      `${this._urlPrefix}recipient-count`,
      body,
      options,
    );
  }
}
